import './product-info.scss';

import { useContext, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { CustomSwitch } from '@/components/Shared/Switch/CustomSwitch';
import { CarInfoModal } from '@/components/Shared/entities';
import { Autocomplete, Input, Select } from '@/components/Shared/ui';
import { BankNames } from '@/constants';
import ChevronDownIcon from '@/media/icons/chevron-down.svg?react';
import { formatPrice } from '@/services/formatter';
import { safeKey } from '@/services/helpers/dict';
import { useAppSelector } from '@/store';
import type { IUsedCar } from '@/types/model.type';

import { CreateTabContext } from '../../../ExistedOrder';
import { ADDITIONAL_PRODUCT_CODES } from '../../constants';
import type { CreateAutoCreditProps } from '../../types/form-types';
import { SentToSwitch } from './entities';
import useSelectsOptions from './hooks/useSelectsOptions';
import useUsedCars from './hooks/useUsedCars';

const ProductInfo: React.FC = () => {
  const { dicts } = useAppSelector((state) => state.dict);

  const { isFieldsFrozen } = useContext(CreateTabContext);
  const { control, trigger, setValue, getValues, clearErrors, resetField } =
    useFormContext<CreateAutoCreditProps>();

  const [dealer, markModelYear, sentTo, additionalProducts] = useWatch({
    control,
    name: ['dealer', 'markModelYear', 'sentTo', 'additionalProducts']
  });

  const { dealers, autoTypes, loanTerms, engineTypes, repaymentTypes } =
    useSelectsOptions({ chosenDealer: dealer });
  const { usedCarsOptions } = useUsedCars();

  const [vehicleInfoModal, setVehicleInfoModal] = useState(false);

  const isModelCommercial =
    dicts?.Models?.getBy.code[safeKey(markModelYear?.model?.code)]
      ?.is_commercial ?? false;

  const setHiddenForBerekeInsurance = (value: boolean) => {
    const targetCodes = [
      ADDITIONAL_PRODUCT_CODES[BankNames.bereke].kasko_3_0,
      ADDITIONAL_PRODUCT_CODES[BankNames.bereke].kasko_1_8,
      ADDITIONAL_PRODUCT_CODES[BankNames.bereke].insurance
    ];

    setValue(
      'additionalProducts',
      additionalProducts.map((item) =>
        targetCodes.includes(item.code)
          ? { ...item, hiddenByAutoType: value }
          : item
      )
    );
  };

  return (
    <div className="product-info">
      <div className="product-info__header">
        <p className="order-step__main-title">Информация о продукте</p>
        <SentToSwitch />
      </div>

      <div className="order-step__grid">
        <Controller
          control={control}
          name="dealer"
          rules={{
            required: 'Пожалуйста, введите данные',
            minLength: { value: 1, message: 'Пожалуйста, введите данные' }
          }}
          render={({ field, fieldState: { error } }) => (
            <Select
              {...field}
              label="Торговая точка"
              selectedValue={dealers.find((item) => item.value === field.value)}
              onChange={(value) => field.onChange(value.value)}
              options={dealers}
              error={error?.message}
              isRequired
              isFrozen
            />
          )}
        />
        <Controller
          control={control}
          name="autoType"
          rules={{
            required: 'Пожалуйста, введите данные',
            minLength: { value: 1, message: 'Пожалуйста, введите данные' }
          }}
          render={({ field, fieldState: { error } }) => (
            <Select
              {...field}
              label="Тип авто"
              selectedValue={autoTypes.find(
                (item) => item.value === field.value
              )}
              onChange={(value) => {
                field.onChange(value.value);
                // Сбрасываем значения выбранного автомобиля
                resetField('markModelYear');
                resetField('usedCar');

                // Скрываем страховку Bereke если выбрана Б/У авто
                setHiddenForBerekeInsurance(value.value === '2');
              }}
              options={autoTypes.filter(
                (autoType) => !(autoType.value === '3' && !isModelCommercial)
              )}
              error={error?.message}
              isFrozen={isFieldsFrozen}
              isRequired
            />
          )}
        />
        {usedCarsOptions.length === 0 && (
          <Controller
            control={control}
            rules={{
              required: 'Пожалуйста, введите данные',
              validate: (value) => {
                if (!value) return 'Пожалуйста, введите данные';
                if (!value.brand) return 'Выберите марку автомобиля';
                if (!value.model) return 'Выберите модель автомобиля';
                if (!value.equipment) return 'Выберите комплектацию автомобиля';
                if (!value.year) return 'Выберите год выпуска автомобиля';
              }
            }}
            render={({ field, fieldState: { error } }) => {
              const value = [
                markModelYear?.brand?.name,
                markModelYear?.model?.name,
                markModelYear?.equipment?.name,
                markModelYear?.year
              ]
                .filter(Boolean)
                .join(', ');
              return (
                <Input
                  {...field}
                  value={value ?? ''}
                  label="Марка, модель и год выпуска"
                  isRequired
                  error={error?.message}
                  endContent={
                    <span className="product-info__arrow">
                      <ChevronDownIcon />
                    </span>
                  }
                  onChange={() => !isFieldsFrozen && setVehicleInfoModal(true)}
                  onClick={() => !isFieldsFrozen && setVehicleInfoModal(true)}
                  isFrozen={isFieldsFrozen}
                  autoComplete="off"
                />
              );
            }}
            name="markModelYear"
          />
        )}

        {/* ------------------------------------------ */}

        <Controller
          control={control}
          name="costCar"
          rules={{
            required: 'Пожалуйста, введите данные',
            validate: (value) => {
              if (!value) return 'Пожалуйста, введите данные';
              if (Number(value.replace(/\D/g, '')) < 100_000)
                return 'Стоимость авто должна быть больше 100 000 ₸';
            }
          }}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              inputMode="numeric"
              value={field.value}
              label="Стоимость авто"
              onChange={(e) => {
                const value = e.target.value.replace(/\s/g, '');
                if (value.match(/\D/) || value.length > 8) return;
                field.onChange(formatPrice(value));
                if (Number(value) >= 100_000) trigger('initialPayment');
              }}
              error={error?.message}
              isFrozen={isFieldsFrozen}
              isRequired
            />
          )}
        />
        <Controller
          control={control}
          name="initialPayment"
          rules={{
            required: 'Пожалуйста, введите данные',
            minLength: { value: 1, message: 'Пожалуйста, введите данные' },
            validate: (value) => {
              const costCar = Number(
                getValues('costCar')?.replace(/\D/g, '') || 0
              );
              const ninetyPercent = costCar * 0.9;
              if (
                costCar >= 100_000 &&
                Number(value.replace(/\D/g, '')) > ninetyPercent
              )
                return `Значение ПВ не должно быть более 90% от стоимости авто (${formatPrice(ninetyPercent) || 0} ₸)`;
            }
          }}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              inputMode="numeric"
              value={field.value}
              label="Первоначальный взнос"
              onChange={(e) => {
                const value = e.target.value.replace(/\s/g, '');
                if (value.match(/\D/) || value.length > 8) return;
                field.onChange(formatPrice(value));
              }}
              error={error?.message}
              isFrozen={isFieldsFrozen}
              isRequired
            />
          )}
        />
        <Controller
          control={control}
          name="loanTerm"
          rules={{
            required: 'Пожалуйста, введите данные',
            minLength: { value: 1, message: 'Пожалуйста, введите данные' },
            validate: (value) => {
              if (+value < 6 || +value > 84)
                return 'Введите значение от 6 до 84';
            }
          }}
          render={({ field, fieldState: { error } }) => (
            <Autocomplete
              {...field}
              inputMode="numeric"
              label="Срок займа"
              value={field.value ?? ''}
              onChange={(value) => {
                field.onChange(value.replace(/\D/g, ''));
              }}
              options={loanTerms}
              error={error?.message}
              isFrozen={isFieldsFrozen}
              isRequired
            />
          )}
        />

        {/* ------------------------------------------ */}

        <Controller
          control={control}
          name="engineType"
          rules={{
            required: 'Пожалуйста, введите данные',
            minLength: { value: 1, message: 'Пожалуйста, введите данные' }
          }}
          render={({ field, fieldState: { error } }) => (
            <Select
              {...field}
              label="Выберите двигатель"
              selectedValue={engineTypes.find(
                (item) => item.value === field.value
              )}
              onChange={(value) => field.onChange(value.value)}
              options={engineTypes}
              error={error?.message}
              isFrozen={isFieldsFrozen}
              isRequired
            />
          )}
        />
        <Controller
          control={control}
          name="paymentScheme"
          rules={{
            required: 'Пожалуйста, введите данные',
            minLength: { value: 1, message: 'Пожалуйста, введите данные' }
          }}
          render={({ field, fieldState: { error } }) => (
            <Select
              {...field}
              label="Тип графика погашений"
              selectedValue={repaymentTypes.find(
                (item) => item.value === field.value
              )}
              onChange={(value) => field.onChange(value.value)}
              options={repaymentTypes}
              error={error?.message}
              isFrozen={isFieldsFrozen}
              isRequired
            />
          )}
        />
        <Controller
          control={control}
          name="proofInCome"
          render={({ field }) => (
            <div className="product-info__switch">
              <CustomSwitch
                value={field.value}
                label="С подтверждением дохода"
                type="firstStep"
                labelTitle="С подтверждением дохода"
                onchangeCheckedHandler={field.onChange}
                fieldsReadOnly={isFieldsFrozen}
              />
            </div>
          )}
        />
      </div>

      {usedCarsOptions.length > 0 && (
        <div>
          <p className="order-step__subtitle order-step__subtitle--with-border">
            Информация о Б/У авто
          </p>

          <div className="order-step__grid">
            {/* Выбор Б/У авто */}
            <Controller
              control={control}
              name="usedCar.title"
              rules={{
                required: 'Пожалуйста, введите данные',
                validate: (value) => {
                  if (!value) return 'Пожалуйста, введите данные';
                  if (!usedCarsOptions.find((item) => item.label === value))
                    return 'Выберите существующий автомобиль';
                }
              }}
              render={({ field, fieldState: { error } }) => (
                <Autocomplete
                  {...field}
                  label="Марка, модель"
                  value={field.value ?? ''}
                  onChange={(value, option: unknown) => {
                    field.onChange(value);
                    if (option) {
                      // Переназначаем тип с IAutoCompleteOption на IUsedCar
                      const usedCar = option as IUsedCar;
                      setValue('usedCar.info', usedCar);
                      // Если год выпуска указан, устанавливаем его
                      if (usedCar.year) setValue('usedCar.year', usedCar.year);
                      // Если VIN код указан, устанавливаем его
                      if (usedCar.vin_code)
                        setValue('usedCar.vinCode', usedCar.vin_code);
                    }
                  }}
                  options={usedCarsOptions}
                  error={error?.message}
                  isFrozen={isFieldsFrozen}
                  isRequired
                />
              )}
            />
            <Controller
              control={control}
              name="usedCar.vinCode"
              rules={{
                required: 'Пожалуйста, введите данные',
                minLength: {
                  value: 17,
                  message: 'VIN код должен состоять из 17 символов'
                },
                validate: (value) => {
                  if (value && /I|O|Q/.test(value))
                    return 'В поле VIN не должно быть букв I, O, Q';
                }
              }}
              render={({ field, fieldState: { error } }) => (
                <Input
                  {...field}
                  label="VIN код"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^A-Za-z0-9]/g, '');
                    if (value.length > 17) return;
                    field.onChange(value.toUpperCase());
                  }}
                  error={error?.message}
                  isFrozen={isFieldsFrozen}
                  isRequired
                />
              )}
            />
            {/* Даем возможность указать год выпуска автомобиля вручную */}
            <Controller
              control={control}
              name="usedCar.year"
              rules={{
                required: 'Пожалуйста, введите данные',
                minLength: { value: 1, message: 'Пожалуйста, введите данные' }
              }}
              render={({ field, fieldState: { error } }) => (
                <Input
                  {...field}
                  inputMode="numeric"
                  label="Год выпуска"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length > 4) return;
                    field.onChange(value);
                  }}
                  error={error?.message}
                  isFrozen={isFieldsFrozen}
                  isRequired
                />
              )}
            />
            <Controller
              control={control}
              name="usedCar.color"
              render={({ field }) => (
                <Input
                  {...field}
                  label="Цвет"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                  }}
                  isFrozen={isFieldsFrozen}
                />
              )}
            />
            <Controller
              control={control}
              name="usedCar.engineCapacity"
              render={({ field }) => (
                <Input
                  {...field}
                  inputMode="numeric"
                  label="Объем двигателя"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length > 4) return;
                    field.onChange(value);
                  }}
                  isFrozen={isFieldsFrozen}
                />
              )}
            />
            <Controller
              control={control}
              name="usedCar.enginePower"
              render={({ field }) => (
                <Input
                  {...field}
                  inputMode="numeric"
                  label="Мощность двигателя"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length > 4) return;
                    field.onChange(value);
                  }}
                  isFrozen={isFieldsFrozen}
                />
              )}
            />
          </div>
        </div>
      )}

      <CarInfoModal
        type={sentTo === 'leasing' ? 'autoCreditLeasing' : 'autoCredit'}
        isOpened={vehicleInfoModal}
        onClose={() => setVehicleInfoModal(false)}
        keyToUse="code"
        onSave={(values) => {
          setValue('markModelYear', {
            brand: {
              name: values.mark.name,
              code: values.mark.code!
            },
            model: {
              name: values.model.name,
              code: values.model.code!
            },
            equipment: {
              name: values.equipment.name,
              uuid: values.equipment.uuid!
            },
            year: values.year,
            dealer: values.dealer
              ? {
                  name: values.dealer.name,
                  code: values.dealer.code!
                }
              : undefined
          });
          if (values.dealer?.code) setValue('dealer', values.dealer.code);
          if (values.model.isCommercial) setValue('autoType', '3');
          clearErrors('markModelYear');
          setVehicleInfoModal(false);
        }}
        defaultValues={
          markModelYear
            ? {
                mark: markModelYear.brand?.name || '',
                model: markModelYear.model?.name || '',
                year: markModelYear.year,
                equipment: markModelYear.equipment?.name || '',
                dealer: markModelYear.dealer?.name
              }
            : undefined
        }
      />
    </div>
  );
};

export default ProductInfo;
